import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  useDisclosure,
  useToast,
  Flex,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Badge,
  IconButton,
  Text,
  Spinner,
  Stack,
  Divider,
  useBreakpointValue
} from '@chakra-ui/react';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

export default function Worship() {
  const [worshipSessions, setWorshipSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { user } = useAuth();
  const lectureSeule = user?.role === 'commissaire' && !user?.is_admin;
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [formData, setFormData] = useState({
    date_culte: new Date().toISOString().split('T')[0],
    nombre_hommes: 0,
    nombre_femmes: 0,
    nombre_enfants: 0,
    montant_offrandes: 0,
    ventes_bibles: 0,
    ventes_cantiques: 0,
    ventes_diverses: 0,
    notes: ''
  });

  useEffect(() => {
    fetchWorshipSessions();
  }, []);

  const fetchWorshipSessions = async () => {
    try {
      setLoading(true);
      const data = await apiService.makeRequest('/worship-sessions');
      setWorshipSessions(data.sessions || data || []);
    } catch (err) {
      console.error('Error fetching worship sessions:', err);
      toast({
        title: 'Erreur',
        description: 'Erreur lors du chargement des cultes',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (session = null) => {
    if (session) {
      setSelectedSession(session);
      setFormData({
        date_culte: session.date_culte.split('T')[0],
        nombre_hommes: session.nombre_hommes || 0,
        nombre_femmes: session.nombre_femmes || 0,
        nombre_enfants: session.nombre_enfants || 0,
        montant_offrandes: session.montant_offrandes || 0,
        ventes_bibles: session.ventes_bibles || 0,
        ventes_cantiques: session.ventes_cantiques || 0,
        ventes_diverses: session.ventes_diverses || 0,
        notes: session.notes || ''
      });
    } else {
      setSelectedSession(null);
      setFormData({
        date_culte: new Date().toISOString().split('T')[0],
        nombre_hommes: 0,
        nombre_femmes: 0,
        nombre_enfants: 0,
        montant_offrandes: 0,
        ventes_bibles: 0,
        ventes_cantiques: 0,
        ventes_diverses: 0,
        notes: ''
      });
    }
    onOpen();
  };

  const handleSubmit = async () => {
    try {
      if (selectedSession) {
        await apiService.updateWorshipSession(selectedSession.id, formData);
        toast({
          title: 'Culte modifié',
          description: 'Les informations du culte ont été mises à jour',
          status: 'success',
          duration: 3000,
        });
      } else {
        await apiService.createWorshipSession(formData);
        toast({
          title: 'Culte enregistré',
          description: 'Les informations du culte ont été enregistrées',
          status: 'success',
          duration: 3000,
        });
      }
      onClose();
      fetchWorshipSessions();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message || 'Erreur lors de l\'enregistrement',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  // Calculer les statistiques globales
  const calculateStats = () => {
    if (worshipSessions.length === 0) return null;

    const totalParticipants = worshipSessions.reduce((sum, session) => 
      sum + (session.nombre_hommes || 0) + (session.nombre_femmes || 0) + (session.nombre_enfants || 0), 0
    );

    const totalOffrandes = worshipSessions.reduce((sum, session) => 
      sum + (parseFloat(session.montant_offrandes) || 0), 0
    );

    const totalVentes = worshipSessions.reduce((sum, session) => 
      sum + (parseFloat(session.ventes_bibles) || 0) + 
          (parseFloat(session.ventes_cantiques) || 0) + 
          (parseFloat(session.ventes_diverses) || 0), 0
    );

    return {
      totalSessions: worshipSessions.length,
      totalParticipants,
      averageParticipants: Math.round(totalParticipants / worshipSessions.length),
      totalOffrandes,
      totalVentes,
      totalRevenu: totalOffrandes + totalVentes
    };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={3}>
        <Heading size="lg">Gestion du Culte</Heading>
        {!lectureSeule && (
          <Button colorScheme="green" onClick={() => handleOpenModal()}>
          + Enregistrer un culte
        </Button>
        )}
      </Flex>

      {/* Statistiques globales */}
      {stats && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Total cultes</StatLabel>
                <StatNumber>{stats.totalSessions}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Participants total</StatLabel>
                <StatNumber>{stats.totalParticipants}</StatNumber>
                <StatLabel fontSize="sm" color="gray.500">
                  Moyenne: {stats.averageParticipants}/culte
                </StatLabel>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Offrandes total</StatLabel>
                <StatNumber color="green.600">{formatAmount(stats.totalOffrandes)}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Ventes total</StatLabel>
                <StatNumber color="blue.600">{formatAmount(stats.totalVentes)}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Liste des cultes */}
      <Card>
        <CardBody p={0}>
          {isMobile ? (
            <Stack spacing={3} p={3} divider={<Divider />}>
              {worshipSessions.length === 0 ? (
                <Text color="gray.500" textAlign="center" py={6}>Aucun culte enregistré</Text>
              ) : (
                worshipSessions.map((session) => {
                  const hommes = session.nombre_hommes || 0;
                  const femmes = session.nombre_femmes || 0;
                  const enfants = session.nombre_enfants || 0;
                  const ventes = (parseFloat(session.ventes_bibles) || 0) +
                                 (parseFloat(session.ventes_cantiques) || 0) +
                                 (parseFloat(session.ventes_diverses) || 0);
                  return (
                    <Box key={session.id}>
                      <Flex justify="space-between" align="flex-start" mb={1} gap={2}>
                        <Text fontWeight="semibold">{formatDate(session.date_culte)}</Text>
                        <Badge colorScheme="blue" fontSize="md">{hommes + femmes + enfants} pers.</Badge>
                      </Flex>
                      <Text fontSize="sm" color="gray.600" mb={1}>
                        {hommes} hommes · {femmes} femmes · {enfants} enfants
                      </Text>
                      <Flex justify="space-between" align="center">
                        <Box>
                          <Text fontSize="sm" color="green.600" fontWeight="semibold">Offrandes : {formatAmount(session.montant_offrandes)}</Text>
                          <Text fontSize="sm" color="blue.600" fontWeight="semibold">Ventes : {formatAmount(ventes)}</Text>
                        </Box>
                        {!lectureSeule && (<Button size="sm" colorScheme="blue" variant="ghost" onClick={() => handleOpenModal(session)}>
                          Modifier
                        </Button>)}
                      </Flex>
                    </Box>
                  );
                })
              )}
            </Stack>
          ) : (
          <TableContainer>
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Date</Th>
                  <Th>Participants</Th>
                  <Th>Hommes</Th>
                  <Th>Femmes</Th>
                  <Th>Enfants</Th>
                  <Th isNumeric>Offrandes</Th>
                  <Th isNumeric>Ventes</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {worshipSessions.length === 0 ? (
                  <Tr>
                    <Td colSpan={8} textAlign="center" py={10}>
                      Aucun culte enregistré
                    </Td>
                  </Tr>
                ) : (
                  worshipSessions.map((session) => {
                    const totalParticipants = (session.nombre_hommes || 0) + 
                                            (session.nombre_femmes || 0) + 
                                            (session.nombre_enfants || 0);
                    const totalVentes = (parseFloat(session.ventes_bibles) || 0) + 
                                       (parseFloat(session.ventes_cantiques) || 0) + 
                                       (parseFloat(session.ventes_diverses) || 0);

                    return (
                      <Tr key={session.id} _hover={{ bg: 'gray.50' }}>
                        <Td fontWeight="medium">{formatDate(session.date_culte)}</Td>
                        <Td>
                          <Badge colorScheme="blue" fontSize="md">
                            {totalParticipants}
                          </Badge>
                        </Td>
                        <Td>{session.nombre_hommes || 0}</Td>
                        <Td>{session.nombre_femmes || 0}</Td>
                        <Td>{session.nombre_enfants || 0}</Td>
                        <Td isNumeric fontWeight="semibold" color="green.600">
                          {formatAmount(session.montant_offrandes)}
                        </Td>
                        <Td isNumeric fontWeight="semibold" color="blue.600">
                          {formatAmount(totalVentes)}
                        </Td>
                        <Td>
                          {!lectureSeule && (<Button 
                            size="sm" 
                            colorScheme="blue" 
                            variant="ghost"
                            onClick={() => handleOpenModal(session)}
                          >
                            Modifier
                          </Button>)}
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </TableContainer>
          )}
        </CardBody>
      </Card>

      {/* Modal d'ajout/modification */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            {selectedSession ? 'Modifier le culte' : 'Enregistrer un culte'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Date du culte</FormLabel>
                <Input
                  type="date"
                  value={formData.date_culte}
                  onChange={(e) => setFormData({ ...formData, date_culte: e.target.value })}
                />
              </FormControl>

              <Box w="full">
                <Heading size="sm" mb={3}>Nombre de participants</Heading>
                <SimpleGrid columns={3} spacing={4}>
                  <FormControl>
                    <FormLabel>Hommes</FormLabel>
                    <Input
                      type="number"
                      min="0"
                      value={formData.nombre_hommes}
                      onChange={(e) => setFormData({ ...formData, nombre_hommes: parseInt(e.target.value) || 0 })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Femmes</FormLabel>
                    <Input
                      type="number"
                      min="0"
                      value={formData.nombre_femmes}
                      onChange={(e) => setFormData({ ...formData, nombre_femmes: parseInt(e.target.value) || 0 })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Enfants</FormLabel>
                    <Input
                      type="number"
                      min="0"
                      value={formData.nombre_enfants}
                      onChange={(e) => setFormData({ ...formData, nombre_enfants: parseInt(e.target.value) || 0 })}
                    />
                  </FormControl>
                </SimpleGrid>
                <Box mt={2} p={2} bg="blue.50" borderRadius="md">
                  <Text fontWeight="bold">
                    Total: {formData.nombre_hommes + formData.nombre_femmes + formData.nombre_enfants} participants
                  </Text>
                </Box>
              </Box>

              <Box w="full">
                <Heading size="sm" mb={3}>Finances</Heading>
                <VStack spacing={3}>
                  <FormControl>
                    <FormLabel>Montant des offrandes (EUR)</FormLabel>
                    <Input
                      type="number"
                      min="0"
                      value={formData.montant_offrandes}
                      onChange={(e) => setFormData({ ...formData, montant_offrandes: parseFloat(e.target.value) || 0 })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Ventes de bibles (EUR)</FormLabel>
                    <Input
                      type="number"
                      min="0"
                      value={formData.ventes_bibles}
                      onChange={(e) => setFormData({ ...formData, ventes_bibles: parseFloat(e.target.value) || 0 })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Ventes de cantiques (EUR)</FormLabel>
                    <Input
                      type="number"
                      min="0"
                      value={formData.ventes_cantiques}
                      onChange={(e) => setFormData({ ...formData, ventes_cantiques: parseFloat(e.target.value) || 0 })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Ventes divers (EUR)</FormLabel>
                    <Input
                      type="number"
                      min="0"
                      value={formData.ventes_diverses}
                      onChange={(e) => setFormData({ ...formData, ventes_diverses: parseFloat(e.target.value) || 0 })}
                    />
                  </FormControl>
                </VStack>
                <Box mt={2} p={2} bg="green.50" borderRadius="md">
                  <Text fontWeight="bold">
                    Total recettes: {formatAmount(
                      formData.montant_offrandes + 
                      formData.ventes_bibles + 
                      formData.ventes_cantiques + 
                      formData.ventes_diverses
                    )}
                  </Text>
                </Box>
              </Box>

              <FormControl>
                <FormLabel>Notes (optionnel)</FormLabel>
                <Input
                  as="textarea"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Remarques, événements particuliers..."
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Annuler
            </Button>
            <Button 
              colorScheme="green" 
              onClick={handleSubmit}
            >
              Enregistrer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}